import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { updateEntity, withEntities } from '@ngrx/signals/entities';
import {
  fetchAllPages,
  withCursorPagination,
  withPickerList,
} from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingBillingItemService } from './generated/billing-billing-item/billing-billing-item.service';
import type {
  BillingItemCreateRequestV1Request,
  BillingV1BillingItemsListParams,
  BillingItemV1,
  PatchedBillingItemUpdateRequestV1Request,
} from './generated/types';

/**
 * TARG-4 pure: injects `BillingBillingItemService` directly, no facade. The
 * generated response shape matches 1:1 what the store needs. Precedent:
 * `MintPlansStore` (feat-mint-plans-crud, 2026-08-30).
 *
 * COMPONENT-DRIVEN: `dt-billing-items` calls `load({...})` on init with the
 * current filters — the store does NOT auto-load in an onInit hook (matches
 * mint-plans; keeps the filter state ownership in the component).
 */
export const BillingItemsStore = signalStore(
  withEntities<BillingItemV1>(),
  withCursorPagination<BillingItemV1, BillingV1BillingItemsListParams>(() => {
    const api = inject(BillingBillingItemService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.billingV1BillingItemsList({ ...query, cursor: cursor ?? undefined });
  }),
  // Picker list: the full active catalog for the item pickers (charge and quote lines,
  // bundles), SEPARATE from the partial cursor entities().
  withPickerList('billingItems', () => {
    const api = inject(BillingBillingItemService);
    return () =>
      fetchAllPages((cursor) =>
        api.billingV1BillingItemsList({ page_size: 1000, active: true, cursor }),
      );
  }),
  withMethods((store, api = inject(BillingBillingItemService)) => ({
    // POST → single `{billing_item}`. Do NOT addEntity into a cursor list:
    // the new row's server-side sort position (id ASC) is unknown → inserting
    // locally desyncs the cursor. Reset to page 1 via the cursor feature's
    // `load` (server-authoritative). Matches MintPlansStore.createMintPlan.
    createBillingItem(body: BillingItemCreateRequestV1Request) {
      return api.billingV1BillingItemsCreate(body).pipe(
        tap(() => {
          store.load({});
        }),
        map((res) => res.billing_item),
      );
    },
    // PATCH → single `{billing_item}` → patch the row IN PLACE. idKey =
    // default `id` (the withEntities selectId). The URL path param `identity`
    // is only needed for routing; the cursor stays valid (no reorder).
    // Matches MintPlansStore.updateMintPlan.
    updateBillingItem(
      body: PatchedBillingItemUpdateRequestV1Request,
      identity: string,
    ) {
      return api
        .billingV1BillingItemsPartialUpdate({ identity }, body)
        .pipe(
          tap((res) => {
            patchState(
              store,
              updateEntity({ id: res.billing_item.id, changes: res.billing_item }),
            );
          }),
          map((res) => res.billing_item),
        );
    },
    // GET of one item, as the catalog has it today. Nothing to keep: the lupa
    // of a quote line shows it and lets it go.
    billingItem(identity: string) {
      return api
        .billingV1BillingItemsRetrieve({ identity })
        .pipe(map((res) => res.billing_item));
    },
  })),
);
